import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Quote, Shield, BadgeCheck, Users, Clock, MessageCircle, Gift } from "lucide-react";
import { trackEvent, FunnelEvents } from "../utils/tracking";

// Kristen's profile image
const KRISTEN_IMAGE = "https://customer-assets.emergentagent.com/job_trust-quiz/artifacts/xk8ju6xh_Brown%20and%20Light%20Brown%2C%20Circle%20Framed%20Instagram%20Profile%20Picture.png";

// Dr. Michelle's photo
const DR_MICHELLE_IMAGE = "https://customer-assets.emergentagent.com/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/tjl6njwr_Screenshot%202026-02-11%20at%207.52.14%E2%80%AFPM.png";

export default function ProductsThankYouPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check if user actually submitted the form
    const submitted = sessionStorage.getItem("productFormSubmitted");
    if (!submitted) {
      navigate("/try-products");
      return;
    }
    
    // Track thank you page view
    trackEvent(FunnelEvents.TRY_PRODUCTS_THANK_YOU);
  }, [navigate]);

  const handleInstagramClick = () => {
    trackEvent(FunnelEvents.INSTAGRAM_CTA_CLICK, { source: 'try_products_thank_you' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-bone to-bone-paper relative">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 texture-overlay opacity-30" />
      
      <div className="relative z-10">
        {/* SECTION: Above-the-Fold */}
        <section className="px-6 py-12 lg:py-16 lg:px-12">
          <div className="w-full max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Column - Copy */}
              <motion.div 
                className="text-center lg:text-left"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Success Icon */}
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-6"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <Check className="w-8 h-8 text-white" />
                </motion.div>
                
                {/* Headline */}
                <h1 
                  className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight mb-5"
                  data-testid="thank-you-title"
                >
                  Thank You! Your Next Step Is Ready.
                </h1>
                
                {/* Subheadline */}
                <p 
                  className="font-inter text-charcoal-light text-base lg:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0"
                  data-testid="thank-you-message"
                >
                  I'll be reaching out personally with your customised product recommendations shortly. If you're ready to check out now or have any questions, send me a direct message on Instagram to speed things up.
                </p>
                
                {/* CTA Button */}
                <motion.a
                  href="https://ig.me/m/iamkristenhinman?ref=w49941361"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 py-4 px-10 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-inter font-semibold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleInstagramClick}
                  data-testid="instagram-cta"
                >
                  Message Me on Instagram
                  <ArrowRight className="w-5 h-5" />
                </motion.a>
              </motion.div>
              
              {/* Right Column - Kristen's Photo */}
              <motion.div 
                className="flex justify-center lg:justify-end"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <img 
                  src={KRISTEN_IMAGE}
                  alt="Kristen Hinman"
                  className="w-64 h-64 lg:w-80 lg:h-80 object-cover rounded-full shadow-2xl border-4 border-white"
                />
              </motion.div>
            </div>
          </div>
        </section>
        
        {/* SECTION 1: What Happens Next */}
        <section className="px-6 py-12 lg:py-16 lg:px-12 bg-bone-paper/50">
          <div className="w-full max-w-4xl mx-auto">
            <motion.h2 
              className="font-playfair text-2xl sm:text-3xl text-charcoal text-center mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              What Happens Next
            </motion.h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { 
                  icon: Clock, 
                  step: 1, 
                  title: "Review Your Profile", 
                  desc: "I'll review your hair profile and customise your shampoo cart based on your specific needs and goals." 
                },
                { 
                  icon: MessageCircle, 
                  step: 2, 
                  title: "Personal Outreach", 
                  desc: "You'll hear from me within 24-48 hours with your personalised product recommendations." 
                },
                { 
                  icon: Gift, 
                  step: 3, 
                  title: "Instant Access", 
                  desc: "Once I send your cart, you'll be able to check out and gain instant access to Richual, our exclusive membership community." 
                },
              ].map((item, idx) => (
                <motion.div 
                  key={idx}
                  className="text-center p-6 bg-bone rounded-xl border border-slate-highlight/30"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-amber-700" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-amber-600 text-white font-playfair text-sm flex items-center justify-center mx-auto mb-3">
                    {item.step}
                  </div>
                  <h3 className="font-playfair text-lg text-charcoal mb-2">{item.title}</h3>
                  <p className="font-inter text-sm text-charcoal-light leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* SECTION 2: Trust Signals and Social Proof */}
        <section className="px-6 py-12 lg:py-16 lg:px-12">
          <div className="w-full max-w-4xl mx-auto">
            {/* Testimonial */}
            <motion.div 
              className="bg-bone-paper rounded-xl p-6 lg:p-8 border-l-4 border-amber-600 mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4">
                <img 
                  src={DR_MICHELLE_IMAGE}
                  alt="Dr. Michelle"
                  className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                />
                <div>
                  <Quote className="w-6 h-6 text-amber-600 mb-2" />
                  <p className="font-inter text-charcoal text-base lg:text-lg italic leading-relaxed mb-3">
                    "Kristen made the process so simple! I received my customised cart and was able to check out quickly. The products are amazing, and being part of Richual has been a game changer."
                  </p>
                  <p className="font-inter text-sm text-charcoal font-semibold">
                    — <strong>Dr. Michelle</strong>, Naturopath
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Richual Membership Highlight */}
            <motion.div 
              className="text-center mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-3 rounded-full border border-amber-200 mb-4">
                <Users className="w-5 h-5 text-amber-700" />
                <span className="font-inter text-sm text-charcoal font-medium">
                  Free Access to <strong>Richual</strong> Membership
                </span>
              </div>
              <p className="font-inter text-charcoal-light text-sm leading-relaxed max-w-2xl mx-auto">
                Remember: As soon as you check out, you'll gain free access to Richual—a mentorship community created for trusted experts and their clients to stay supported and scale smarter.
              </p>
            </motion.div>
            
            {/* Trust Badges */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-5 py-3 rounded-full border-2 border-amber-500/50 bg-amber-50/50">
                <BadgeCheck className="w-5 h-5 text-amber-600" />
                <span className="font-inter text-sm text-charcoal font-medium">
                  Clinically tested and trusted by professionals
                </span>
              </div>
              
              <div className="flex items-center gap-2 px-5 py-3 rounded-full border-2 border-blue-800/50 bg-blue-50/50">
                <Shield className="w-5 h-5 text-blue-800" />
                <span className="font-inter text-sm text-charcoal font-semibold">
                  Risk-free with a 30-Day Money-Back Guarantee
                </span>
              </div>
            </div>
          </div>
        </section>
        
        {/* SECTION 3: Final CTA */}
        <section className="px-6 py-14 lg:py-20 lg:px-12 bg-gradient-to-b from-bone-paper to-bone">
          <div className="w-full max-w-3xl mx-auto text-center">
            <motion.p 
              className="font-inter text-charcoal text-base lg:text-lg leading-relaxed mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ready to get started? Message me on Instagram now to speed up the process, or wait for my recommendations to arrive. Either way, you're covered!
            </motion.p>
            
            <motion.a
              href="https://ig.me/m/iamkristenhinman?ref=w49941361"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 py-4 px-10 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-inter font-semibold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleInstagramClick}
              data-testid="final-instagram-cta"
            >
              Message Me on Instagram
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </div>
        </section>
      </div>
    </div>
  );
}
